import { useState, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Menu, X, LogOut, ExternalLink, ChevronDown, Eye, LayoutGrid } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import FeaturesDialog from "./FeaturesDialog";
import RoleScopeBadge, { roleMeta } from "./RoleScopeBadge";
import api from "@/lib/api";

function PublicProfileLink({ user }) {
  const [open, setOpen] = useState(false);
  const hasEnhancer = ["enhancer", "vendor"].includes(user?.role) || user?.is_enhancer;
  const managesId = user?.manages_location_id || "";
  const { data: locations = [] } = useQuery({
    queryKey: ["public-locations"],
    queryFn: () => api.get("/locations").then((r) => r.data),
    enabled: !!managesId,
    staleTime: 10 * 60 * 1000,
  });
  const center = locations.find((l) => l.id === managesId);
  const profiles = [];
  if (hasEnhancer) profiles.push({ key: "enhancer", label: "My Enhancer Profile", url: `/enhancers/${user.slug || user.id}` });
  if (managesId) profiles.push({ key: "center", label: "My Center Page", url: `/locations/${center?.slug || managesId}` });
  if (!profiles.length) return null;

  const openProfile = (url) => { window.open(url, "_blank", "noopener"); setOpen(false); };

  if (profiles.length === 1) {
    return (
      <button data-testid="shell-public-profile-link" onClick={() => openProfile(profiles[0].url)}
        className="mt-2.5 w-full flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-xs text-white/60 hover:text-white hover:border-white/30 transition-colors">
        <Eye size={13} className="shrink-0" /> <span className="flex-1 text-left">View My Public Profile</span> <ExternalLink size={12} className="shrink-0 opacity-60" />
      </button>
    );
  }
  return (
    <div className="mt-2.5 relative">
      <button data-testid="shell-public-profile-link" onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-xs text-white/60 hover:text-white hover:border-white/30 transition-colors">
        <Eye size={13} className="shrink-0" /> <span className="flex-1 text-left">View My Public Profile</span>
        <ChevronDown size={13} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 rounded-xl bg-white/[0.06] border border-white/10 p-1" data-testid="shell-public-profile-menu">
          {profiles.map((p) => (
            <button key={p.key} data-testid={`shell-public-profile-${p.key}`} onClick={() => openProfile(p.url)}
              className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors">
              <span className="flex-1 text-left">{p.label}</span> <ExternalLink size={12} className="shrink-0 opacity-60" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const LOGO = "https://customer-assets.emergentagent.com/job_eyw-admin-suite/artifacts/3ze0y6kq_EnhancingYourWorld%20site%20logo%20with%20transparent%20background%20web%20version.png";

function NavItem(props) {
  const btn = <NavButton {...props} />;
  if (!props.item.desc) return btn;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{btn}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-[260px] text-xs" data-testid={`nav-tip-${props.item.key}`}>{props.item.desc}</TooltipContent>
    </Tooltip>
  );
}

const NavButton = forwardRef(function NavButton({ item, active, accent, onSelect, ...rest }, ref) {
  const Icon = item.icon;
  return (
    <button
      ref={ref}
      {...rest}
      data-testid={`nav-item-${item.key}`}
      onClick={() => onSelect(item.key)}
      className={`group w-full flex items-center gap-2.5 rounded-xl pl-3 pr-2 py-2.5 text-sm transition-colors duration-200 ${
        active ? "bg-white/10 text-white font-semibold" : "text-white/60 hover:text-white hover:bg-white/[0.06]"
      }`}
      style={active ? { boxShadow: `inset 3px 0 0 ${accent}` } : undefined}
    >
      {Icon && <Icon size={16} className="shrink-0" style={active ? { color: accent } : undefined} />}
      <span className="flex-1 min-w-0 text-left truncate">{item.label}</span>
      {item.badge > 0 && (
        <span
          data-testid={`nav-badge-${item.key}`}
          className="ml-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full text-[10px] font-bold text-white"
          style={{ backgroundColor: accent }}
        >
          {item.badge}
        </span>
      )}
    </button>
  );
});

function NavGroup({ group, activeKey, accent, onSelect, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = group.icon;
  const single = group.items.length === 1;
  if (single) {
    return <NavItem item={{ ...group.items[0], icon: group.items[0].icon || Icon }} active={activeKey === group.items[0].key} accent={accent} onSelect={onSelect} />;
  }
  return (
    <div>
      <button
        data-testid={`nav-group-${group.key}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-[11px] uppercase tracking-widest font-semibold text-white/40 hover:text-white/70 transition-colors"
      >
        {Icon && <Icon size={14} className="shrink-0" />}
        <span className="flex-1 text-left truncate">{group.label}</span>
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="space-y-0.5 mt-0.5">
          {group.items.map((it) => (
            <NavItem key={it.key} item={it} active={activeKey === it.key} accent={accent} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardShell({
  role,
  brandTitle,
  scopeName,
  nav = [],
  activeKey,
  onSelect,
  contextSwitcher,
  quickActions = [],
  user,
  onLogout,
  children,
}) {
  const nav_ = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const meta = roleMeta(role);
  const accent = meta.hex;
  const activeGroupKey = (nav.find((g) => g.items.some((it) => it.key === activeKey)) || {}).key;

  const select = (key) => {
    onSelect(key);
    setMobileOpen(false);
  };

  const SidebarContent = (
    <div className="flex flex-col h-full bg-[#1A242B] w-72">
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-2.5 mb-4">
          <img src={LOGO} alt="Enhancing Your World" className="h-9 w-auto" />
          <span className="font-serif text-base text-white leading-tight truncate">{brandTitle}</span>
          <button data-testid="shell-mobile-close" onClick={() => setMobileOpen(false)} className="ml-auto lg:hidden text-white/60 hover:text-white p-2 -mr-2 rounded-lg min-h-[44px] min-w-[44px] grid place-items-center">
            <X size={20} />
          </button>
        </div>
        <RoleScopeBadge role={role} scopeName={scopeName} />
        {contextSwitcher && <div className="mt-4">{contextSwitcher}</div>}
        <PublicProfileLink user={user} />
      </div>

      <nav data-testid="shell-nav" className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
        {nav.map((group) => (
          <NavGroup
            key={group.key}
            group={group}
            activeKey={activeKey}
            accent={accent}
            onSelect={select}
            defaultOpen={group.key === activeGroupKey}
          />
        ))}
      </nav>

      <div className="p-4 border-t border-white/10 space-y-3">
        <button data-testid="shell-profile-link" onClick={() => { setMobileOpen(false); nav_("/settings/profile"); }}
          className="w-full flex items-center gap-2.5 min-w-0 rounded-xl -mx-1 px-1 py-1 text-left hover:bg-white/[0.07] transition-colors"
          title="Account & profile settings">
          <span className="grid place-items-center h-9 w-9 rounded-full text-white text-sm font-semibold shrink-0" style={{ backgroundColor: accent }}>
            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{user?.name || "You"}</p>
            <p className="text-xs text-white/50 truncate">{user?.email}</p>
          </div>
        </button>
        <div className="flex items-center gap-2">
          <button data-testid="shell-view-site" onClick={() => nav_("/")} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/40 px-3 py-2 text-xs transition-colors">
            <ExternalLink size={13} /> View site
          </button>
          <button data-testid="shell-logout" onClick={onLogout} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-xs transition-colors">
            <LogOut size={13} /> Log out
          </button>
        </div>
      </div>
    </div>
  );

  const activeItem = nav.flatMap((g) => g.items).find((it) => it.key === activeKey);

  return (
    <TooltipProvider delayDuration={250}>
    <div className="min-h-screen bg-[#F9F7F3] lg:flex">
      {mobileOpen && (
        <button data-testid="shell-mobile-backdrop" onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-black/40 lg:hidden" aria-label="Close menu" />
      )}
      <aside
        className={`fixed lg:sticky inset-y-0 lg:top-0 left-0 z-50 h-screen w-72 shrink-0 transform transition-transform duration-200 lg:translate-x-0 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {SidebarContent}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-md border-b border-[#E5E0D8] flex items-center gap-3 px-4 sm:px-6 lg:px-8">
          <button data-testid="shell-mobile-toggle" onClick={() => setMobileOpen(true)} className="lg:hidden text-[#1A242B] p-2 -ml-2 rounded-lg hover:bg-black/5 min-h-[44px] min-w-[44px] grid place-items-center">
            <Menu size={22} />
          </button>
          <div className="min-w-0">
            <h1 data-testid="shell-active-title" className="font-serif text-lg sm:text-xl text-[#1A242B] tracking-tight truncate">
              {activeItem?.label || brandTitle}
            </h1>
          </div>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
            <button data-testid="shell-features-btn" onClick={() => setFeaturesOpen(true)} title="Everything you can do here"
              className="inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 min-h-[44px] text-sm font-medium border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75] hover:text-[#144F75] transition-colors">
              <LayoutGrid size={15} /> <span className="hidden md:inline">All features</span>
            </button>
            {quickActions.map((a) => (
              <button
                key={a.testid}
                data-testid={a.testid}
                onClick={a.onClick}
                title={a.label}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 min-h-[44px] text-sm font-medium transition-colors ${
                  a.primary ? "text-white" : "border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75] hover:text-[#144F75]"
                }`}
                style={a.primary ? { backgroundColor: accent } : undefined}
              >
                {a.icon && <a.icon size={15} />}
                <span className="hidden md:inline">{a.label}</span>
              </button>
            ))}
          </div>
        </header>

        <main data-testid="shell-main" className="flex-1 px-4 sm:px-6 lg:px-8 py-6 lg:py-8 max-w-[1400px] w-full">
          {children}
        </main>
      </div>
      <FeaturesDialog open={featuresOpen} onOpenChange={setFeaturesOpen} groups={nav} onSelect={(k) => { setFeaturesOpen(false); onSelect(k); }} />
    </div>
    </TooltipProvider>
  );
}
